import React, { useRef, useState } from 'react';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type TooltipProps = {
  /** Texto do tooltip. Vazio ou nulo não abre. Para condicionar, use `disabled` ou `text={cond ? x : undefined}`, nunca `text={cond && x}`. */
  text?: React.ReactNode;
  /** Lado do gatilho em que o tooltip abre. */
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /** Largura máxima: número em px, `'25vw'` ou `'none'` (texto que não deve quebrar). */
  maxWidth?: number | string;
  /** Alinhamento do texto. */
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  /** Mantém aberto ao clicar no gatilho (usado pelo `Info`). Nos ícones de ação, deixe desligado: o tooltip fecha no clique. */
  keepOpenOnClick?: boolean;
  /** Não abre. O gatilho continua igual. */
  disabled?: boolean;
  /** Onde o tooltip é renderizado: o elemento ou o id dele. Use na tela cheia (ex.: Card de Metas). */
  container?: HTMLElement | string;
  /** Classes do balão. */
  className?: string;
  /** Esconde a seta. */
  hideArrow?: boolean;
  /** Distância do gatilho, em px. */
  sideOffset?: number;
  /** O gatilho: um único elemento que aceite ref (Button do portal, LucideIcon, elemento nativo). */
  children: React.ReactElement;
};

/**
 * Texto curto que aparece ao passar o mouse ou focar o gatilho. Para conteúdo rico (lista, tabela), use o `HoverCard`; para o ícone "i" de ajuda, use o `Info`.
 * @import import Tooltip from 'src/componentes/tooltip';
 */
const Tooltip = ({
  text,
  placement = 'top',
  maxWidth = 200,
  textAlign = 'center',
  keepOpenOnClick,
  disabled,
  container,
  className,
  hideArrow,
  sideOffset,
  children,
}: TooltipProps) => {
  const [open, setOpen] = useState(false);
  const pressingTrigger = useRef(false);

  // Sem texto, a árvore continua a mesma (só não abre): devolver o children puro
  // remontaria o gatilho quando o texto aparece ou some (ex.: hint do lookup).
  const inactive = disabled || text == null || text === '';

  // O Radix pede para fechar no pointerdown/click do gatilho e no pointerdown
  // "fora" do conteúdo, que também dispara no gatilho. Com keepOpenOnClick,
  // enquanto o gatilho está pressionado esses pedidos são ignorados. Sem
  // preventDefault, o clique continua dando foco e acionando o <label>/ancestral.
  const handleOpenChange = (next: boolean) => {
    if (next && inactive) return;
    if (next || !keepOpenOnClick || !pressingTrigger.current) setOpen(next);
  };

  const triggerProps = keepOpenOnClick && {
    onPointerDown: () => {
      pressingTrigger.current = true;
      // pointercancel substitui o pointerup quando o navegador assume o gesto
      // (rolagem por toque, arraste nativo); sem ele, a ref ficaria presa.
      const release = () => {
        document.removeEventListener('pointerup', release);
        document.removeEventListener('pointercancel', release);
        setTimeout(() => (pressingTrigger.current = false));
      };
      document.addEventListener('pointerup', release);
      document.addEventListener('pointercancel', release);
    },
  };

  // container pode ser o id de um elemento que ainda não existe quando a tela
  // monta (ex.: o card de metas em tela cheia); por isso é buscado ao abrir.
  const portalContainer =
    typeof container === 'string'
      ? (open && document.getElementById(container)) || undefined
      : container;

  return (
    <UITooltip open={!inactive && open} onOpenChange={handleOpenChange}>
      <TooltipTrigger asChild {...triggerProps}>
        {children}
      </TooltipTrigger>
      {!inactive && (
        <TooltipContent
          side={placement}
          container={portalContainer}
          className={className}
          hideArrow={hideArrow}
          sideOffset={sideOffset}
          style={{ maxWidth, textAlign }}
        >
          {text}
        </TooltipContent>
      )}
    </UITooltip>
  );
};

export { TooltipProvider };

export default Tooltip;
